import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Button,
  Alert,
  Snackbar
} from '@mui/material';

import {
  Close,
  Download,
  VideoLibrary,
  Image,
  BrokenImage,
  Error
} from '@mui/icons-material';

const MediaViewer = ({ open, onClose, mediaFiles = [], submissionInfo }) => {
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [imageErrors, setImageErrors] = useState(new Set());

  const debugUrl = (url) => {
    if (!url) return 'URL is null or undefined';
    
    console.log('URL debug analysis:', {
      original: url,
      length: url.length,
      startsWithHttp: url.startsWith('http'),
      startsWithUploads: url.includes('/uploads/'),
      hasFileExtension: /\.(jpg|jpeg|png|gif|mp4|mov|avi|pdf)$/i.test(url),
      containsInvalidChars: /[<>:"|?*]/.test(url),
      parts: url.split('/').filter(part => part)
    });
    
    return url;
  };

  const handleDownload = async (mediaUrl, filename) => {
    try {
      setLoading(true);
      setDownloadError(null);
      
      console.log('Download attempt:', { mediaUrl, filename });
      
      // FIX: Comprehensive URL validation
      if (!mediaUrl || typeof mediaUrl !== 'string') {
        throw new Error('Invalid media URL: URL is missing or not a string');
      }

      // Trim and clean the URL
      mediaUrl = mediaUrl.trim();
      
      // Check for common URL issues
      if (mediaUrl.length < 10) {
        throw new Error(`URL too short (${mediaUrl.length} characters): ${mediaUrl}`);
      }

      if (mediaUrl.includes('undefined') || mediaUrl.includes('null')) {
        throw new Error(`URL contains invalid values: ${mediaUrl}`);
      }

      let downloadUrl = mediaUrl;
      
      // If URL is relative, make it absolute by adding the server base URL
      if (downloadUrl && !downloadUrl.startsWith('http')) {
        // Ensure the URL starts with a slash
        if (!downloadUrl.startsWith('/')) {
          downloadUrl = `/${downloadUrl}`;
        }
        downloadUrl = `https://propertyearn.net${downloadUrl}`;
        console.log('Converted relative URL to absolute:', downloadUrl);
      }
      
      // FIX: Validate the final URL with better error handling
      try {
        const urlObj = new URL(downloadUrl);
        if (!urlObj.hostname || !urlObj.pathname) {
          throw new Error('URL missing hostname or pathname');
        }
        console.log('URL validation passed:', urlObj.toString());
      } catch (e) {
        throw new Error(`Invalid URL format: ${downloadUrl}. Error: ${e.message}`);
      }

      // FIX: Add authentication token to the request
      const token = localStorage.getItem('adminToken');
      const headers = {
        'Authorization': `Bearer ${token}`
      };

      console.log('Final download URL:', downloadUrl);
      
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      try {
        console.log('Making HEAD request to check file existence...');
        const headResponse = await fetch(downloadUrl, {
          method: 'HEAD',
          headers: headers,
          signal: controller.signal
        });
        
        console.log('HEAD response status:', headResponse.status, headResponse.statusText);
        
        if (!headResponse.ok) {
          throw new Error(`File not found (${headResponse.status} ${headResponse.statusText})`);
        }
      } catch (headError) {
        console.warn('HEAD request failed:', headError.message);
        // Continue with GET request even if HEAD fails
      } finally {
        clearTimeout(timeoutId);
      }
      
      // Create new AbortController for GET request
      const getController = new AbortController();
      const getTimeoutId = setTimeout(() => getController.abort(), 30000);
      
      // Proceed with download using GET request
      console.log('Making GET request for download...');
      const response = await fetch(downloadUrl, {
        headers: headers,
        signal: getController.signal
      }).catch(fetchError => {
        if (fetchError.name === 'AbortError') {
          throw new Error('Download timed out after 30 seconds');
        }
        throw new Error(`Network error: ${fetchError.message}`);
      });
      
      clearTimeout(getTimeoutId);
      
      console.log('GET response status:', response.status, response.statusText);
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty (0 bytes)');
      }
      
      console.log('Download successful, blob size:', blob.size, 'bytes');
      
      // Create a safe filename
      const safeFilename = filename || 'download';
      const cleanFilename = safeFilename.replace(/[^a-zA-Z0-9.-]/g, '_');
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = cleanFilename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadError(`Download failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleImageError = (mediaId, url) => {
    console.error(`Image failed to load: ${url}`);
    setImageErrors(prev => new Set(prev).add(mediaId));
  };

  const getMediaDisplayUrl = (media) => {
    if (!media.url || typeof media.url !== 'string') {
      console.error('Invalid media URL:', media.url);
      return null;
    }
    
    let displayUrl = media.url;
    
    // FIX: Handle relative URLs by converting to absolute for display
    if (displayUrl && !displayUrl.startsWith('http') && !displayUrl.startsWith('data:')) {
      // Ensure the URL starts with a slash
      if (!displayUrl.startsWith('/')) {
        displayUrl = `/${displayUrl}`;
      }
      displayUrl = `https://propertyearn.net${displayUrl}`;
    }
    
    return displayUrl;
  };

  const isImageBroken = (mediaId) => {
    return imageErrors.has(mediaId);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Generate fallback image for broken images
  const generateFallbackImage = (text) => {
    return `data:image/svg+xml;base64,${btoa(`
      <svg width="200" height="150" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#f5f5f5"/>
        <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="14" 
              fill="#999" text-anchor="middle" dy="0.35em">
          ${text}
        </text>
      </svg>
    `)}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              Media Files ({mediaFiles.length})
            </Typography>
            {submissionInfo && (
              <Typography variant="body2" color="text.secondary">
                Submission by {submissionInfo.submitterName} • Status: {submissionInfo.status}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} disabled={loading}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Error Snackbar */}
        <Snackbar 
          open={!!downloadError} 
          autoHideDuration={6000} 
          onClose={() => setDownloadError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="error" onClose={() => setDownloadError(null)}>
            {downloadError}
          </Alert>
        </Snackbar>

        <Box sx={{ mt: 2 }}>
          {/* Show message when no media files */}
          {(!mediaFiles || mediaFiles.length === 0) && (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Error sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Media Files
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This submission doesn't contain any media files.
              </Typography>
            </Box>
          )}

          {/* Media Grid - Only show when there are files */}
          {mediaFiles && mediaFiles.length > 0 && (
            <>
              {/* Debug info - helpful for troubleshooting */}
              {process.env.NODE_ENV === 'development' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="caption">
                    Debug: {mediaFiles.length} files found. Check console for detailed info.
                  </Typography>
                </Alert>
              )}

              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                gap: 2,
                maxHeight: '60vh',
                overflowY: 'auto'
              }}>
                {mediaFiles.map((media) => {
                  const displayUrl = getMediaDisplayUrl(media);
                  const isBroken = isImageBroken(media.id);
                  const hasValidUrl = displayUrl && displayUrl.length > 10;
                  
                  // Debug logging for each media file
                  console.log('Media debug:', {
                    id: media.id,
                    originalUrl: media.url,
                    displayUrl: displayUrl,
                    filename: media.filename
                  });
                  debugUrl(media.url);
                  
                  return (
                    <Box key={media.id} sx={{ position: 'relative' }}>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: isBroken || !hasValidUrl ? 'error.main' : 'divider',
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: hasValidUrl ? 'pointer' : 'default',
                          '&:hover': hasValidUrl ? { boxShadow: 2 } : {}
                        }}
                        onClick={() => hasValidUrl && setSelectedMedia({...media, displayUrl})}
                      >
                        {hasValidUrl && media.type === 'image' && !isBroken ? (
                          <img
                            src={displayUrl}
                            alt={media.filename}
                            style={{ 
                              width: '100%', 
                              height: '150px', 
                              objectFit: 'cover',
                              backgroundColor: '#f5f5f5'
                            }}
                            onError={() => handleImageError(media.id, displayUrl)}
                            onLoad={(e) => {
                              console.log('Image loaded successfully:', displayUrl);
                            }}
                          />
                        ) : (
                          <Box sx={{ 
                            width: '100%', 
                            height: '150px', 
                            bgcolor: isBroken || !hasValidUrl ? 'error.light' : 'grey.100',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column'
                          }}>
                            {!hasValidUrl ? (
                              <>
                                <Error sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />
                                <Typography variant="caption" color="error.main">
                                  Invalid URL
                                </Typography>
                                <Typography variant="caption" color="error.main" sx={{ fontSize: '0.6rem', mt: 0.5 }}>
                                  {media.url?.substring(0, 30)}...
                                </Typography>
                              </>
                            ) : isBroken ? (
                              <>
                                <BrokenImage sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />
                                <Typography variant="caption" color="error.main">
                                  Failed to load
                                </Typography>
                              </>
                            ) : media.type === 'video' ? (
                              <>
                                <VideoLibrary sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                                <Typography variant="caption" color="text.secondary">
                                  Video File
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Image sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                                <Typography variant="caption" color="text.secondary">
                                  {media.mime_type || 'File'}
                                </Typography>
                              </>
                            )}
                          </Box>
                        )}
                        
                        <Box sx={{ p: 1 }}>
                          <Typography 
                            variant="caption" 
                            noWrap 
                            sx={{ 
                              display: 'block', 
                              width: '100%',
                              color: isBroken || !hasValidUrl ? 'error.main' : 'text.primary'
                            }}
                          >
                            {media.filename || 'Unnamed file'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                            <Chip 
                              label={media.type} 
                              size="small" 
                              sx={{ height: 20, fontSize: '0.7rem' }}
                              color={isBroken || !hasValidUrl ? 'error' : 'default'}
                            />
                            {media.file_size && (
                              <Chip 
                                label={formatFileSize(media.file_size)} 
                                size="small" 
                                sx={{ height: 20, fontSize: '0.7rem' }}
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                      
                      <IconButton
                        size="small"
                        sx={{ 
                          position: 'absolute', 
                          top: 4, 
                          right: 4, 
                          bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'background.default' }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (hasValidUrl) {
                            handleDownload(displayUrl, media.filename);
                          }
                        }}
                        disabled={loading || !hasValidUrl}
                      >
                        {loading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Download fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}

          {/* Selected Media View */}
          {selectedMedia && (
            <Dialog 
              open={!!selectedMedia} 
              onClose={() => setSelectedMedia(null)} 
              maxWidth="lg" 
              fullWidth
            >
              <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">
                    {selectedMedia.filename || 'Media Preview'}
                  </Typography>
                  <IconButton onClick={() => setSelectedMedia(null)}>
                    <Close />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent>
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  {selectedMedia.type === 'image' ? (
                    <img
                      src={selectedMedia.displayUrl}
                      alt={selectedMedia.filename}
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '70vh',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        console.error('Failed to load selected image:', selectedMedia.displayUrl);
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <Box sx={{ 
                      width: '100%', 
                      height: '400px', 
                      bgcolor: 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column'
                    }}>
                      <VideoLibrary sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        Video Preview Not Available
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Use the download button to save the video file.
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={() => handleDownload(selectedMedia.displayUrl, selectedMedia.filename)}
                      disabled={loading}
                    >
                      Download
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => window.open(selectedMedia.displayUrl, '_blank')}
                    >
                      Open in New Tab
                    </Button>
                  </Box>
                  
                  {/* File info */}
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" component="div">
                      <strong>URL:</strong> {selectedMedia.displayUrl}
                    </Typography>
                    <Typography variant="caption" component="div">
                      <strong>Type:</strong> {selectedMedia.type} • {selectedMedia.mime_type}
                    </Typography>
                    {selectedMedia.file_size && (
                      <Typography variant="caption" component="div">
                        <strong>Size:</strong> {formatFileSize(selectedMedia.file_size)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </DialogContent>
            </Dialog>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MediaViewer;